import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Search } from "lucide-react";
import { fetchClientAccess, type ClientAccessFilters, type ClientAccessResult } from "../services/api";
import { usePageTitle } from "../hooks/usePageTitle";

/**
 * What resellers' clients read and wrote of their suppliers' data (tenant plan
 * phase 16): every request to the supplier routes, refusals included, newest
 * first. Narrow by client, supplier, person, surface, day range, or refusals
 * only (a run of refusals is what probing looks like).
 */

const SURFACES = [
  "suppliers", "supplier", "summary", "orders", "order", "invoices", "invoice", "stock",
  "products", "reports", "report", "customer", "customer_details", "unlisted"
];

export function ClientAccessPage() {
  usePageTitle("Client access");
  const [filters, setFilters] = useState<ClientAccessFilters>({});
  const [applied, setApplied] = useState<ClientAccessFilters>({});
  const [data, setData] = useState<ClientAccessResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    fetchClientAccess(applied)
      .then((d) => { if (active) setData(d); })
      .catch((e) => { if (active) setError(e instanceof Error ? e.message : "Could not load the client access log."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [applied]);

  const set = (k: keyof ClientAccessFilters, v: string | boolean) => setFilters((f) => ({ ...f, [k]: v || undefined }));

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Operations</span>
          <h1>Client access</h1>
          <p className="muted">Every read and write a reseller's client made of its supplier's data, refusals included, and everything a reseller looked at while viewing a client's workspace as the client sees it.</p>
        </div>
      </header>

      <form
        className="card"
        style={{ padding: 16 }}
        onSubmit={(e) => {
          e.preventDefault();
          setApplied(filters);
        }}
      >
        <div className="row wrap" style={{ gap: 12, alignItems: "flex-end" }}>
          <label style={{ flex: "1 1 240px" }}>
            <span className="muted" style={{ fontSize: 12 }}>Client workspace id</span>
            <input value={filters.clientTenantId ?? ""} onChange={(e) => set("clientTenantId", e.target.value.trim())} placeholder="Client id" />
          </label>
          <label style={{ flex: "1 1 240px" }}>
            <span className="muted" style={{ fontSize: 12 }}>Supplier workspace id</span>
            <input value={filters.supplierTenantId ?? ""} onChange={(e) => set("supplierTenantId", e.target.value.trim())} placeholder="Supplier id" />
          </label>
          <label style={{ flex: "1 1 200px" }}>
            <span className="muted" style={{ fontSize: 12 }}>Person's email</span>
            <input type="email" value={filters.email ?? ""} onChange={(e) => set("email", e.target.value.trim())} placeholder="name@company.co.za" />
          </label>
          <label style={{ flex: "0 1 170px" }}>
            <span className="muted" style={{ fontSize: 12 }}>Surface</span>
            <select value={filters.surface ?? ""} onChange={(e) => set("surface", e.target.value)}>
              <option value="">Every surface</option>
              {SURFACES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label style={{ flex: "0 1 150px" }}>
            <span className="muted" style={{ fontSize: 12 }}>From</span>
            <input type="date" value={filters.from ?? ""} onChange={(e) => set("from", e.target.value)} />
          </label>
          <label style={{ flex: "0 1 150px" }}>
            <span className="muted" style={{ fontSize: 12 }}>To</span>
            <input type="date" value={filters.to ?? ""} onChange={(e) => set("to", e.target.value)} />
          </label>
          <label className="row" style={{ gap: 6, flex: "0 0 auto" }}>
            <input type="checkbox" checked={!!filters.refused} onChange={(e) => set("refused", e.target.checked)} />
            <span>Refused only</span>
          </label>
          <button type="submit" className="btn btn-primary"><Search size={14} /> Show</button>
        </div>
      </form>

      {data && data.bySurface.length > 0 ? (
        <div className="card" style={{ padding: 16 }}>
          <div className="row wrap" style={{ gap: 8 }}>
            {data.bySurface.map((s) => (
              <span key={s.surface} className={`pill ${s.refused > 0 ? "warn" : "accent"}`}>
                {s.surface}: {s.count}{s.refused > 0 ? `, ${s.refused} refused` : ""}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <div className="table-wrap">
        {loading ? (
          <div className="empty-state"><p className="muted">Loading client access…</p></div>
        ) : error ? (
          <div className="empty-state"><p className="muted">{error}</p></div>
        ) : !data || data.items.length === 0 ? (
          <div className="empty-state">
            <Eye size={28} />
            <h3>Nothing yet</h3>
            <p>Reads by resellers' clients appear here as they happen.</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Client</th>
                <th>Person</th>
                <th>Supplier</th>
                <th>Surface</th>
                <th>Id</th>
                <th>Answer</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.id}>
                  <td>{new Date(r.at).toLocaleString("en-ZA")}</td>
                  <td><Link to={`/companies/${r.clientTenantId}`}>{r.clientName ?? r.clientTenantId}</Link></td>
                  <td>
                    {r.userEmail ?? r.userId ?? ""}
                    {r.viewedAs ? <span className="pill warn" style={{ marginLeft: 6 }}>viewing as the client</span> : null}
                  </td>
                  <td>{r.supplierName ?? r.supplierTenantId ?? ""}</td>
                  <td>{r.method === "GET" ? r.surface : `${r.method} ${r.surface}`}</td>
                  <td className="faint" style={{ fontSize: 11 }}>{r.targetId ?? ""}</td>
                  <td><span className={`pill ${r.status >= 400 ? "warn" : "success"}`}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
